import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import {
	shadingFailed, shadingRecalcAccepted, shadingRecalcRequested, setFacetShadingState,
} from 'store/slices/DsmShadingSlice';
import { FacetEditShadingStatus, recalculateShading } from 'api/editDsm';

/**
 * Kick off the background shading rebuild a facet edit just made necessary.
 *
 * The edit endpoint decides whether one is owed (NSRDB energy source + an edit that
 * changes flux: a pitch/azimuth edit on any facet, a created/reshaped facet, a
 * ground-mount rack change) and says so by returning a FacetEditShadingStatus;
 * in-house callers pass that response straight through, and a missing one means
 * nothing to do. From the 202 on, dsmShadingListener owns the lifecycle:
 * 3s edit-dsm/status poll → changePanel once the new production lands.
 */
export default function useStartShadingRecalc() {
	const dispatch = useDispatch<AppDispatch>();
	const uuid = useSelector((state: RootState) => state.roofData.data.uuid);

	return useCallback(async (editRes?: FacetEditShadingStatus) => {
		if (!editRes || !uuid) return;
		// "recomputing" now, and supersede any watch still polling a previous facet run.
		dispatch(shadingRecalcRequested());
		try {
			dispatch(setFacetShadingState(editRes));
			dispatch(shadingRecalcAccepted(await recalculateShading(uuid)));
		} catch {
			dispatch(shadingFailed({ message: 'Could not start shading recompute' }));
		}
	}, [dispatch, uuid]);
}
